import { Users, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { TenantSelector } from '../../../components/tenant-selector/TenantSelector';
import type {
  TenantSelectionConflicts,
  TenantSelectorRowData,
} from '../../../components/tenant-selector/types';
import { DialogBackdrop } from '../../../components/ui/dialog-backdrop';
import { TenantSelectionValidationStatus } from '../edit-automation-template/tenant-selection-control/TenantSelectionControl';
import styles from '../edit-automation-template/tenant-selection-control/TenantSelectionControl.module.css';
import type { TenantSelectionType } from '../engage-automations-tab/templates.types';

interface SegmentTenantSelectionControlProps {
  disabled?: boolean;
  tenantRowData: Record<string, TenantSelectorRowData>;
  conflicts: TenantSelectionConflicts | null;
  tenantSelectionType: TenantSelectionType;
  selectedTenantIds: string[];
  onChange: (type: TenantSelectionType, ids: string[]) => void;
  isDirty?: boolean;
  isLoading?: boolean;
}

export const SegmentTenantSelectionControl: React.FC<SegmentTenantSelectionControlProps> = ({
  disabled = false,
  tenantRowData,
  conflicts,
  tenantSelectionType,
  selectedTenantIds,
  onChange,
  isDirty = false,
  isLoading,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localSelectionType, setLocalSelectionType] = useState<TenantSelectionType>('all');
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);

  const openDialog = useCallback(() => {
    setLocalSelectionType(tenantSelectionType);
    setLocalSelectedIds(selectedTenantIds);
    setDialogOpen(true);
  }, [tenantSelectionType, selectedTenantIds]);

  const closeDialog = () => setDialogOpen(false);

  const handleConfirm = () => {
    onChange(localSelectionType, localSelectedIds);
    setDialogOpen(false);
  };

  const handleLocalChange = (type: TenantSelectionType, ids: string[]) => {
    setLocalSelectionType(type);
    setLocalSelectedIds(ids);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className={`${styles.tenantButton} ${isDirty ? styles.tenantButtonDirty : ''}`}
        disabled={disabled}
        title="Tenant selection"
        aria-label="Tenant selection">
        <Users className={styles.tenantIcon} />
        {isDirty && <span className={styles.dirtyDot} />}
      </button>

      {dialogOpen && (
        <>
          <DialogBackdrop onClose={closeDialog} />
          <dialog className={styles.dialog} open>
            <header>
              <hgroup>
                <h2>Tenant Selection</h2>
                <p>Choose which tenants receive this segment when you save.</p>
              </hgroup>
              <button type="button" onClick={closeDialog} className={styles.closeButton}>
                <X />
              </button>
            </header>

            <main>
              <TenantSelector
                tenantSelectionType={localSelectionType}
                selectedTenantIds={localSelectedIds}
                onChange={handleLocalChange}
                allTenantsSelectionDescription="New tenants will automatically receive this segment when they're added."
                selectedTenantsSelectionDescription="Only the tenants you select below will receive this segment. New tenants will not receive it unless you add them or they are added to an automation which uses this segment."
                disabled={disabled}
                tenantRowData={tenantRowData}
                isLoading={isLoading}
              />

              {localSelectionType === 'selected' && localSelectedIds.length > 0 && conflicts && (
                <TenantSelectionValidationStatus conflicts={conflicts} isValidating={false} />
              )}
            </main>

            <footer className={styles.dialogFooter}>
              <button type="button" onClick={closeDialog} className={styles.cancelButton}>
                Cancel
              </button>
              <button type="button" onClick={handleConfirm} className={styles.confirmButton}>
                OK
              </button>
            </footer>
          </dialog>
        </>
      )}
    </>
  );
};
